import { motion } from 'framer-motion';
import { Icon } from '~/commonComponents/Icon/Icon';
import { Colors } from '~/themes/colors';
import { useAccordionContext } from './Accordion';
import type { IAccordionItemProps } from './interfaces';
import { Column } from '../Column';
import { Text } from '../Text';
import { Row } from '../Row';

const chevronVariants = {
  open: { rotate: 180 },
  closed: { rotate: 0 },
};

const bodyVariants = {
  open: { maxHeight: 'unset', opacity: 1, paddingTop: 16, paddingBottom: 4 },
  closed: { maxHeight: 0, opacity: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0 },
};

export const AccordionItem = ({
  children,
  description,
  itemKey,
  style,
  title,
}: IAccordionItemProps) => {
  const { isItemOpen, onToggle } = useAccordionContext();
  const isOpen = isItemOpen(itemKey);

  return (
    <Column backgroundColor="transparent">
      <button
        type="button"
        aria-expanded={isOpen}
        style={{
          all: 'unset',
          boxSizing: 'border-box',
          cursor: 'pointer',
          width: '100%',
          ...style,
        }}
        onClick={() => onToggle(itemKey)}
      >
        <Row alignItems="center" justifyContent="space-between">
          {typeof title === 'string' ? (
            <Text bold fontSize={18}>
              {title}
            </Text>
          ) : (
            title
          )}
          <motion.div
            animate={isOpen ? 'open' : 'closed'}
            transition={{ duration: 0.25 }}
            variants={chevronVariants}
          >
            <Icon name="chevron-down" size={18} color={Colors.secondary_500} />
          </motion.div>
        </Row>
        {description && isOpen && (
          <Text color={Colors.neutral_500} marginTop={4}>
            {description}
          </Text>
        )}
      </button>

      <motion.div
        initial={false}
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0.25 }}
        variants={bodyVariants}
        style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 8, ...style }}
      >
        {children}
      </motion.div>
    </Column>
  );
};
